import { Component } from 'react'
import * as cn from 'classnames'
import * as moment from 'moment'
import * as pick from 'lodash/pick'
import { change, Field, getFormValues, reduxForm } from 'redux-form'
import { getSaveButtonFormProps, shouldAsyncValidate } from 'helpers/formHelpers'
import { asyncValidator } from 'helpers/schemaHelpers'
import schema from 'schemas/transactionEditFormSchema'
import { DATE_MASK } from 'constants/forms'
import { ReduxFormProps, StoreDispatch } from 'interfaces/component'
import * as tableStyles from 'styles/molecules/tables/Table2.css'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import { DATE_FORMAT_US } from 'constants/datetime'
import { connect } from 'react-redux'
import { State } from 'interfaces/state'
import { EditableTransactionKeys, Transaction, FullMoneyflowTyped } from 'interfaces/moneyflow'
import { toggleTransactionEditActionCreator } from 'actions/moneyflowActions'
import { TypedMoneyflowCategory } from 'interfaces/meta'
import TextButton from 'components/atoms/buttons/TextButton'
import * as styles from 'styles/molecules/forms/TransactionEditForm.css'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import Button from 'components/features/pfm/Button/Button'
import { TransactionEditFormValues } from 'interfaces/form'
import { formatUSCurrency, getEditTransactionFormName } from 'helpers/formatHelpers'
import { EDITABLE_TRANSACTION_KEYS } from 'constants/moneyflow'
import { submitFormActionCreator } from 'actions/formActions'
import { KEY_CODES } from 'helpers/keyboardHelpers'
import { OuterEventTrackerContext, OuterEventTrackerContextType } from 'components/utils/OuterEventTracker'
import withContext from 'hocs/withContext/withContext'
import { isTransactionEditable } from 'helpers/moneyflowHelpers'
import TransactionCategoryDropdown from 'components/atoms/dropdowns/TransactionCategoryDropdown'
import { TransactionName } from 'components/features/transactions/TransactionName/TransactionName'

type TransactionEditFormStateProps =
  & ReduxFormProps
  & {
    selectedCategory: TypedMoneyflowCategory
  }

type TransactionEditFormDispatchProps =
  & Pick<ReduxFormProps, 'onSubmit'>
  & {
    cancelTransactionEdit: () => void
    setCategory: (category: TypedMoneyflowCategory) => void
  }

type TransactionEditFormOwnProps = FullMoneyflowTyped & {
  outerEventTracker: OuterEventTrackerContextType
  transaction: Transaction
  isMobile?: boolean
}

type TransactionEditFormProps =
  & TransactionEditFormStateProps
  & TransactionEditFormDispatchProps
  & TransactionEditFormOwnProps

const DATE_FIELD_PROPS = {
  label: 'Date',
  type: 'tel',
  mask: DATE_MASK,
  placeholder: DATE_FORMAT_US,
  keepCharPositions: true,
  autoFocus: true,
  qaMarker: 'date-input',
  qaType: 'tel',
}

const DESCRIPTION_FIELD_PROPS = {
  label: 'Description',
  type: 'text',
  qaMarker: 'description-input',
}

const AMOUNT_FIELD_PROPS = {
  label: 'Amount',
  type: 'tel',
  mask: createNumberMask({ prefix: '$', allowDecimal: true, allowNegative: true }),
  maxLength: 13,
  qaMarker: 'amount-input',
  qaType: 'tel',
}

@reduxForm({
  asyncValidate: asyncValidator(schema),
  shouldAsyncValidate,
  // the following two flags are a solution for a reinitialization issue
  // when start editing another transaction in a moment of editing the first one.
  destroyOnUnmount: false,
  enableReinitialize: true,
})
class TransactionEditFormComponent extends Component<TransactionEditFormProps, {}> {
  render () {
    const {
      isMobile,
      transaction,
      cancelTransactionEdit,
      handleSubmit,
    } = this.props

    const tableClassName = cn(styles.rowFieldsTable, tableStyles.transactions)

    return (
      <form
        className={ cn(styles.transactionEditForm, {
          [styles.isMobile]: isMobile,
        }) }
        onSubmit={ handleSubmit }
      >
        <div className={ styles.content }>
          { isMobile && (
            <div className={ styles.title }>Edit Transaction</div>
          )}
          <table className={ tableClassName }>
            <tbody>
              <tr className={ styles.row }>
                <td className={ cn(styles.rowField, styles.textFieldWrapper) }>
                  <Field
                    name='date'
                    props={ DATE_FIELD_PROPS }
                    component={ TextFieldWrapped }
                  />
                </td>
                <td className={ cn(styles.rowField, styles.textFieldWrapper) }>
                  <Field
                    name='description'
                    props={ DESCRIPTION_FIELD_PROPS }
                    component={ TextFieldWrapped }
                  />
                </td>
                { !isMobile && (
                  <td className={ styles.rowField }>
                    <span>
                      <TransactionName transaction={ transaction } />
                    </span>
                  </td>
                )}
                <td className={ cn(styles.rowField, styles.categoryField) }>
                  { this.renderCategory() }
                </td>
                <td className={ cn(styles.rowField, styles.textFieldWrapper) }>
                  <Field
                    name='amount'
                    props={ AMOUNT_FIELD_PROPS }
                    component={ TextFieldWrapped }
                  />
                </td>
                { !isMobile && (
                  <td className={ styles.rowField }>
                    <div className={ styles.commandColumnContent }>
                      <div className={ styles.editButtonWrapper }>
                        <TextButton>Edit</TextButton>
                      </div>
                      <div className={ styles.cancelButtonWrapper }>
                        <TextButton onClick={ cancelTransactionEdit }>
                          <span className={ styles.cancelButtonContent }>
                            ×
                          </span>
                        </TextButton>
                      </div>
                    </div>
                  </td>
                )}
              </tr>
            </tbody>
          </table>
          <div className={ styles.buttons }>
            <div className={ styles.buttonWrapper }>
              <Button
                size='responsive'
                text={ isMobile ? 'Update Transaction' : 'Save' }
                submit
                { ...getSaveButtonFormProps(this.props) }
              />
            </div>
            <div className={ styles.buttonWrapper }>
              <Button
                size='responsive'
                text='Cancel'
                style='hollow'
                onClick={ cancelTransactionEdit }
              />
            </div>
          </div>
        </div>
      </form>
    )
  }

  renderCategory = () => {
    const {
      transaction,
      selectedCategory,
      isMobile,
    } = this.props

    const label = isMobile ? (
      <div className={ styles.categoryLabel }>Category</div>
    ) : null
    const category = isTransactionEditable(transaction)
      ? (
        <TransactionCategoryDropdown
          selected={ selectedCategory }
          onSelect={ this.handleSelectCategory }
        />
      )
      : selectedCategory.value

    return (
      <>
        { label }
        { category }
      </>
    )
  }

  componentDidMount () {
    this.props.outerEventTracker.addOuterEventListener('keydown', this.onKeydown, null)
  }

  componentWillUnmount () {
    this.props.outerEventTracker.removeOuterEventListener('keydown', this.onKeydown)
  }

  private handleSelectCategory = (category: TypedMoneyflowCategory): void => {
    this.props.setCategory(category)
  }

  private onKeydown = (e: KeyboardEvent) => {
    if (e.keyCode === KEY_CODES.ESC) {
      this.props.cancelTransactionEdit()
    }
  }
}

const mapStateToProps = (state: State, ownProps: TransactionEditFormOwnProps): TransactionEditFormStateProps => {
  const { transaction, type } = ownProps
  const editableTransactionValues: Pick<Transaction, EditableTransactionKeys> = pick(transaction, EDITABLE_TRANSACTION_KEYS)

  const initialValues: TransactionEditFormValues = {
    ...editableTransactionValues,
    amount: formatUSCurrency(editableTransactionValues.amount, 'penny'),
    date: moment(transaction.date).format(DATE_FORMAT_US),
  }

  const formValues = getFormValues<TransactionEditFormValues, State>(getEditTransactionFormName(type))(state) || initialValues

  return {
    selectedCategory: formValues.category,
    form: getEditTransactionFormName(type),
    initialValues,
  }
}

const mapDispatchToProps = (dispatch: StoreDispatch, ownProps: TransactionEditFormOwnProps): TransactionEditFormDispatchProps => {
  const { transaction, type } = ownProps
  const formName = getEditTransactionFormName(type)

  return {
    onSubmit: (values: TransactionEditFormValues) => {
      dispatch(submitFormActionCreator(formName, values, { transaction }))
      return {}
    },
    cancelTransactionEdit: () => dispatch(toggleTransactionEditActionCreator(type, transaction.uid, false)),
    setCategory: (category: TypedMoneyflowCategory) => dispatch(change(formName, 'category', category)),
  }
}

const TransactionEditForm = connect(mapStateToProps, mapDispatchToProps)(TransactionEditFormComponent)

const WithContextComponent = withContext(OuterEventTrackerContext, 'outerEventTracker')(TransactionEditForm)
export default WithContextComponent
